import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import TradeTable from '../../components/TradeTable';

const RANGE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '7d',    label: '7 days' },
  { id: '30d',   label: '30 days' },
  { id: '90d',   label: '90 days' },
  { id: 'all',   label: 'All' },
];

const TYPE_OPTIONS = [
  { id: '',     label: 'All' },
  { id: 'buy',  label: 'Buy' },
  { id: 'sell', label: 'Sell' },
];

const OUTCOME_OPTIONS = [
  { id: '',     label: 'All' },
  { id: 'win',  label: 'Wins' },
  { id: 'loss', label: 'Losses' },
  { id: 'be',   label: 'Breakeven' },
];

function toLocalInput(d) {
  // YYYY-MM-DDTHH:mm for datetime-local
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function rangeFor(preset) {
  const now = new Date();
  if (preset === 'all')   return { from: '', to: '' };
  if (preset === 'today') {
    const start = new Date(now); start.setHours(0,0,0,0);
    return { from: toLocalInput(start), to: toLocalInput(now) };
  }
  const map = { '7d': 7, '30d': 30, '90d': 90 };
  const days = map[preset];
  if (!days) return { from: '', to: '' };
  const start = new Date(now.getTime() - days * 86400_000);
  return { from: toLocalInput(start), to: toLocalInput(now) };
}

export default function TradeHistory() {
  const [trades, setTrades] = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const [filters, setFilters] = useState({
    from: '', to: '', symbol: '', type: '', outcome: '', preset: 'all',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: 50 };
    if (filters.from)    params.from    = new Date(filters.from).toISOString();
    if (filters.to)      params.to      = new Date(filters.to).toISOString();
    if (filters.symbol)  params.symbol  = filters.symbol.trim();
    if (filters.type)    params.type    = filters.type;
    if (filters.outcome) params.outcome = filters.outcome;
    api.get('/customer/trades/history', { params })
      .then(r => { setTrades(r.data.trades); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [page, filters.from, filters.to, filters.symbol, filters.type, filters.outcome]);

  function applyPreset(preset) {
    const { from, to } = rangeFor(preset);
    setFilters(f => ({ ...f, preset, from, to }));
    setPage(1);
  }

  function clearAll() {
    setFilters({ from: '', to: '', symbol: '', type: '', outcome: '', preset: 'all' });
    setPage(1);
  }

  const activeCount = useMemo(() =>
    (filters.from ? 1 : 0) + (filters.to ? 1 : 0) +
    (filters.symbol ? 1 : 0) + (filters.type ? 1 : 0) + (filters.outcome ? 1 : 0)
  , [filters]);

  return (
    <>
      <div className="ct-page-header">
        <div>
          <span className="eyebrow">Customer</span>
          <h1>Trade History</h1>
          <div className="subtitle">Every closed trade your EAs have made</div>
        </div>
      </div>

      <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title">Filters</h5>
            <div className="card-category" style={{ marginTop: 4 }}>{total} trade{total === 1 ? '' : 's'} match</div>
          </div>

          <div className="card-body filter-panel">
            <div className="filter-row" style={{ gridTemplateColumns: '1fr' }}>
              <div className="filter-group">
                <div className="filter-label">
                  <span>Date Range</span>
                  <span className="filter-label-hint">when the trade closed</span>
                </div>
                <div className="filter-controls">
                  <div className="pill-row">
                    {RANGE_PRESETS.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        className={`pill ${filters.preset === p.id ? 'pill-active' : ''}`}
                        onClick={() => applyPreset(p.id)}
                      >{p.label}</button>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <input
                      type="datetime-local"
                      className="form-control filter-input"
                      value={filters.from}
                      onChange={e => { setFilters(f => ({ ...f, from: e.target.value, preset: 'custom' })); setPage(1); }}
                    />
                    <input
                      type="datetime-local"
                      className="form-control filter-input"
                      value={filters.to}
                      onChange={e => { setFilters(f => ({ ...f, to: e.target.value, preset: 'custom' })); setPage(1); }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="filter-row" style={{ marginTop: 14 }}>
              <div className="filter-group">
                <div className="filter-label">
                  <span>Symbol</span>
                  <span className="filter-label-hint">exact match</span>
                </div>
                <div className="filter-controls">
                  <input
                    type="text"
                    className="form-control filter-input"
                    placeholder="e.g. XAUUSD"
                    value={filters.symbol}
                    onChange={e => { setFilters(f => ({ ...f, symbol: e.target.value })); setPage(1); }}
                  />
                </div>
              </div>

              <div className="filter-group">
                <div className="filter-label">
                  <span>Direction</span>
                  <span className="filter-label-hint">buy or sell</span>
                </div>
                <div className="filter-controls">
                  <div className="pill-row">
                    {TYPE_OPTIONS.map(o => (
                      <button
                        key={o.id || 'any'}
                        type="button"
                        className={`pill ${filters.type === o.id ? 'pill-active' : ''}`}
                        onClick={() => { setFilters(f => ({ ...f, type: o.id })); setPage(1); }}
                      >{o.label}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="filter-row" style={{ marginTop: 14, gridTemplateColumns: '1fr' }}>
              <div className="filter-group">
                <div className="filter-label">
                  <span>Outcome</span>
                  <span className="filter-label-hint">filter by result</span>
                </div>
                <div className="filter-controls">
                  <div className="pill-row">
                    {OUTCOME_OPTIONS.map(o => (
                      <button
                        key={o.id || 'any'}
                        type="button"
                        className={`pill ${filters.outcome === o.id ? 'pill-active' : ''}`}
                        onClick={() => { setFilters(f => ({ ...f, outcome: o.id })); setPage(1); }}
                      >{o.label}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {activeCount > 0 && (
              <div className="filter-footer">
                <span className="text-muted small">
                  {activeCount} filter{activeCount > 1 ? 's' : ''} active · {total} trade{total === 1 ? '' : 's'}
                </span>
                <button className="btn btn-sm btn-link p-0 ml-2" onClick={clearAll}>
                  Clear all
                </button>
              </div>
            )}
          </div>

          <div className="card-body">
            {loading ? <p className="text-muted">Loading...</p> : (
              <>
                <TradeTable trades={trades} showStatus />
                <div className="d-flex justify-content-between mt-2">
                  <span className="text-muted small">Page {page} — {trades.length} / {total}</span>
                  <div>
                    <button className="btn btn-sm btn-secondary mr-1" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                    <button className="btn btn-sm btn-secondary" disabled={trades.length < 50} onClick={() => setPage(p => p + 1)}>Next</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
}
